import type { DragEvent, FunctionComponent, KeyboardEvent, ReactNode } from "react";
import { useRef, useState } from "react";
import { cn } from "./cn";
import { FOCUS_RING } from "./focus";

export interface DropzoneProps {
  /** Receives the newly picked/dropped files (the caller owns the list). */
  onFiles: (files: File[]) => void;
  multiple?: boolean;
  /** Pick/drop whole folders; dropped files are named by their path inside the folder. */
  directory?: boolean;
  /** Same syntax as `<input accept>`; also filters dropped files (and folder contents, by file name). */
  accept?: string;
  /** Receives the dropped/picked files that `accept` filtered out, so the caller can say why. */
  onReject?: (files: File[]) => void;
  disabled?: boolean;
  className?: string;
  children?: ReactNode;
}

const matchesAccept = (file: File, accept?: string) => {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  return accept
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .some((token) => {
      if (token.startsWith(".")) return name.endsWith(token);
      if (token.endsWith("/*")) return file.type.startsWith(token.slice(0, -1));
      return file.type === token;
    });
};

const readEntries = (reader: FileSystemDirectoryReader) =>
  new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));

/** Recursively collect the files under a dropped entry, named by their folder-relative path. */
const collectEntry = async (entry: FileSystemEntry): Promise<File[]> => {
  if (entry.isFile) {
    const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
    const path = entry.fullPath.replace(/^\//, "");
    return path && path !== file.name
      ? [new File([file], path, { type: file.type, lastModified: file.lastModified })]
      : [file];
  }
  if (!entry.isDirectory) return [];

  const reader = (entry as FileSystemDirectoryEntry).createReader();
  // readEntries returns batches (≤100 in Chrome) until it yields an empty one.
  const readAll = async (acc: FileSystemEntry[]): Promise<FileSystemEntry[]> => {
    const batch = await readEntries(reader);
    return batch.length === 0 ? acc : readAll([...acc, ...batch]);
  };
  const children = await readAll([]);
  const nested = await Promise.all(children.map(collectEntry));
  return nested.flat();
};

const filesFromDrop = async (event: DragEvent<HTMLElement>, directory?: boolean) => {
  if (!directory) return Array.from(event.dataTransfer.files);
  const entries = Array.from(event.dataTransfer.items)
    .map((item) => item.webkitGetAsEntry())
    .filter((entry): entry is FileSystemEntry => entry !== null);
  const nested = await Promise.all(entries.map(collectEntry));
  return nested.flat();
};

/** Click-or-drag file picker (antd `Upload.Dragger` without the upload/list parts). */
export const Dropzone: FunctionComponent<DropzoneProps> = ({
  onFiles,
  multiple,
  directory,
  accept,
  onReject,
  disabled,
  className,
  children,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const emit = (files: File[]) => {
    const accepted = files.filter((file) => matchesAccept(file, accept));
    const rejected = files.filter((file) => !accepted.includes(file));
    const picked = multiple ? accepted : accepted.slice(0, 1);
    if (rejected.length > 0) onReject?.(rejected);
    if (picked.length > 0) onFiles(picked);
  };

  const handleDragOver = (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    if (!disabled) setDragging(true);
  };

  const handleDrop = async (event: DragEvent<HTMLElement>) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    emit(await filesFromDrop(event, directory));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    inputRef.current?.click();
  };

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={handleKeyDown}
      onDragOver={handleDragOver}
      onDragLeave={(event) => {
        // Moving over a child fires dragleave on the zone too; only clear when the pointer really left it.
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        setDragging(false);
      }}
      onDrop={(event) => void handleDrop(event)}
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-noa bg-noa-hover p-4 text-center transition-colors",
        "hover:border-noa-primary-hover",
        FOCUS_RING,
        dragging && "border-noa-primary-strong",
        disabled && "cursor-not-allowed opacity-60 hover:border-noa",
        className
      )}
    >
      <input
        ref={inputRef}
        type="file"
        hidden
        multiple={multiple}
        accept={directory ? undefined : accept}
        disabled={disabled}
        {...(directory ? { webkitdirectory: "", directory: "" } : {})}
        onChange={(event) => {
          emit(Array.from(event.target.files ?? []));
          // Reset so picking the same file/folder again still fires `change`.
          event.target.value = "";
        }}
      />
      {children}
    </div>
  );
};
